/**
 * usePersonnelRolesQueries — single source of truth for the Job Positions
 * (personnel roles) cache.
 *
 * Why this file exists (R30 rule 1 "one useQuery per endpoint" + rule 11
 * "centralise invalidation graphs"):
 *
 * The very same `GET /personnel-roles` list used to be cached under three
 * unrelated keys — `['company-personnel', scope, 'roles']` (New Candidate,
 * personnel forms, hiring modals), `['company-timesheet', scope, 'roles']` and
 * `['config', scope, 'personnel-roles-list']` (daily journal) — plus the derived
 * variants (`config/personnel-roles-catalog`, `budget/master-data`,
 * `candidates/hr-config-roles`). Creating a job position in RoleManager only
 * invalidated the catalog key, so every other screen kept serving its own stale
 * copy for up to `staleTime`. Logging out did not help either: logout is
 * declarative (no page reload) and the query cache is a module singleton, so the
 * next login reused the exact same cache entries under the same
 * `tenant:username` scope.
 *
 * Contract now:
 * - every consumer of the plain list uses `usePersonnelRolesQuery` (ONE key);
 * - every job-position mutation calls `usePersonnelRolesInvalidator()` /
 *   `invalidatePersonnelRoles(queryClient)`, which busts the shared list AND the
 *   derived caches in one shot.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useQueryScope } from '../utils/queryScope';

export const PERSONNEL_ROLES_ROOT = 'personnel-roles';

const sharedQueryDefaults = {
  retry: 0,
};

/**
 * `[root, section]` pairs whose cached payload is derived from the job-positions
 * table. Keys are `[ROOT, scope, section, ...params]`, so matching on root +
 * section covers every scope and every parametrised variant (budget context
 * area, driver-licence policy per role id, ...).
 *
 * The legacy `config/personnel-roles-list` entry and the role entries under
 * `company-personnel` and `company-timesheet` are kept here on purpose: a browser
 * tab that was open before this change still holds data under those keys.
 */
const ROLE_DERIVED_KEYS = [
  [PERSONNEL_ROLES_ROOT, 'list'],
  ['config', 'personnel-roles-catalog'],
  ['config', 'personnel-roles-list'],
  ['company-personnel', 'roles'],
  ['company-timesheet', 'roles'],
  ['budget', 'master-data'],
  ['candidates', 'hr-config-roles'],
  ['candidates', 'driver-license-policy'],
];

/** Shared cache key for `GET /personnel-roles`. */
export function personnelRolesListKey(scope) {
  return [PERSONNEL_ROLES_ROOT, scope, 'list'];
}

export function isPersonnelRolesQueryKey(queryKey) {
  if (!Array.isArray(queryKey) || queryKey.length < 3) return false;
  const [root, , section] = queryKey;
  return ROLE_DERIVED_KEYS.some(([r, s]) => r === root && s === section);
}

/**
 * Bust every cache entry derived from job positions. Active queries refetch
 * immediately; inactive ones are marked stale so the next mount (e.g. opening
 * "New Candidate") refetches instead of painting the old list.
 */
export function invalidatePersonnelRoles(queryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => isPersonnelRolesQueryKey(query.queryKey),
  });
}

/** All job positions (`GET /personnel-roles`) — shared by every consumer. */
export function usePersonnelRolesQuery({ enabled = true } = {}) {
  const scope = useQueryScope();
  return useQuery({
    queryKey: personnelRolesListKey(scope),
    queryFn: async () => {
      const { data } = await api.get('/personnel-roles');
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

/** `() => Promise` to call after creating / editing / deleting a job position. */
export function usePersonnelRolesInvalidator() {
  const queryClient = useQueryClient();
  return useCallback(() => invalidatePersonnelRoles(queryClient), [queryClient]);
}
